import React, { useCallback } from 'react';
import { SyutSearchGrid, type SyutSearchGridColumn } from 'components/ui';
import type { TaxMasterPage, TaxMasterQuery, TaxMasterSummary } from './taxMaster.types';
import './tax-master.css';

/**
 * The mapping-side selector (ADR-0019 §8): an app's item/service master embeds this to pick
 * the tax master an item maps to. Built on the template's `SyutSearchGrid` — the canonical
 * search popup (server-side paging, sortable columns, filters, recently used) — so it looks
 * and behaves like every other picker in the app.
 *
 * THE CONTRACT: store the chosen `rateCode` (with an effective date) on the app's own
 * mapping table. Never a percentage, never a row id (ADR-0019 §3).
 */
export interface TaxMasterPickerProps {
  isOpen: boolean;
  /** Injected transport: typically `(q) => taxMasterApi.list(jurisdictionCode, q)`. */
  load: (query: TaxMasterQuery) => Promise<TaxMasterPage>;
  onSelect: (master: TaxMasterSummary) => void;
  onClose: () => void;
  title?: string;
  /** Key for the recently-used list; give each embedding screen its own. */
  recentlyUsedKey?: string;
}

const COLUMNS: SyutSearchGridColumn<TaxMasterSummary>[] = [
  { key: 'rateSummary', header: 'Rate', width: '90px', sortable: true, render: (v: string) => <span className="txm-mono">{v}</span> },
  { key: 'name', header: 'Name', width: '220px', sortable: true, searchable: true },
  { key: 'rateCode', header: 'Code', width: '140px', sortable: true, searchable: true, render: (v: string) => <span className="txm-mono">{v}</span> },
  {
    key: 'kind', header: 'Type', width: '110px', sortable: true,
    render: (_v: string, m: TaxMasterSummary) => (
      <>
        <span className={`txm-badge txm-badge-${m.kind.toLowerCase()}`}>{m.kind}</span>
        {m.hasCess && <span className="txm-badge txm-badge-cess">+cess</span>}
      </>
    ),
  },
  {
    key: 'nextChangeOn', header: 'Next change', width: '110px', sortable: true,
    render: (v: string | null) => (v ? <span className="txm-badge txm-badge-future">{v.slice(0, 10)}</span> : <span className="txm-dim">—</span>),
  },
];

export const TaxMasterPicker: React.FC<TaxMasterPickerProps> = ({
  isOpen, load, onSelect, onClose, title, recentlyUsedKey = 'tax-master-picker',
}) => {
  const loadData = useCallback(async (params: {
    searchQuery: string;
    filters: Record<string, string>;
    sortKey?: string;
    sortDirection?: 'asc' | 'desc';
    page: number;
    pageSize: number;
  }) => {
    const page = await load({
      page: params.page,
      pageSize: params.pageSize,
      searchTerm: params.searchQuery || undefined,
      kind: params.filters.kind || undefined,
      sortBy: params.sortKey,
      sortDescending: params.sortDirection === 'desc',
    });
    return { data: page.items, total: page.totalCount };
  }, [load]);

  return (
    <SyutSearchGrid<TaxMasterSummary>
      isOpen={isOpen}
      onClose={onClose}
      onSelect={(m) => { onSelect(m); onClose(); }}
      title={title ?? 'Select Tax Master'}
      columns={COLUMNS}
      loadData={loadData}
      rowKey="rateCode"
      filters={[
        { key: 'kind', label: 'Type', type: 'select', options: [{ value: 'FLAT', label: 'Flat' }, { value: 'SLAB', label: 'Slab' }] },
      ]}
      enableRecentlyUsed
      recentlyUsedKey={recentlyUsedKey}
      searchPlaceholder="Search by name or code…"
      emptyMessage="No tax masters match."
    />
  );
};

export default TaxMasterPicker;
